/**
 * Expectations page - main page for managing price expectations
 */

import { SectionErrorBoundary } from '@/components/common/SectionErrorBoundary'
import { ExpectationForm } from '@/components/expectations/ExpectationForm'
import { ExpectationList } from '@/components/expectations/ExpectationList'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useDeleteExpectation, useExpectations } from '@/services/api/expectations'
import type { PriceExpectation } from '@/types/expectation'
import { IconPlus } from '@tabler/icons-react'
import { useState } from 'react'
import { toast } from 'sonner'

export default function Expectations() {
  const [showForm, setShowForm] = useState(false)
  const [editingExpectation, setEditingExpectation] = useState<PriceExpectation | null>(null)

  const { data: expectations = [], isLoading, refetch } = useExpectations()
  const deleteMutation = useDeleteExpectation()
  const handleDelete = async (expectation: PriceExpectation) => {
    if (confirm(`Bạn có chắc chắn muốn xóa kỳ vọng này?`)) {
      try {
        await deleteMutation.mutateAsync(expectation.id)
        refetch()
        toast.success('Đã xóa kỳ vọng thành công')
      } catch (error) {
        console.error('Failed to delete expectation:', error)
        toast.error('Không thể xóa kỳ vọng. Vui lòng thử lại.')
      }
    }
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingExpectation(null)
    refetch()
    toast.success(
      editingExpectation ? 'Đã cập nhật kỳ vọng thành công' : 'Đã tạo kỳ vọng thành công'
    )
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 lg:py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Kỳ vọng Giá</h1>
        <Button
          onClick={() => {
            setEditingExpectation(null)
            setShowForm(true)
          }}
          className="w-full sm:w-auto min-h-[44px]"
        >
          <IconPlus className="h-4 w-4 mr-2" stroke={1.5} />
          Thêm kỳ vọng
        </Button>
      </div>

      <SectionErrorBoundary sectionName="Danh sách kỳ vọng">
        <ExpectationList
          expectations={expectations}
          onEdit={exp => {
            setEditingExpectation(exp)
            setShowForm(true)
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
        />
      </SectionErrorBoundary>

      {(showForm || editingExpectation) && (
        <div className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">
                {editingExpectation ? 'Sửa kỳ vọng' : 'Thêm kỳ vọng mới'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ExpectationForm
                {...(editingExpectation ? { expectation: editingExpectation } : {})}
                onSuccess={handleFormSuccess}
                onCancel={() => {
                  setShowForm(false)
                  setEditingExpectation(null)
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
